import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TrafficLight from "@/components/TrafficLight";
import { Search, ArrowLeft } from "lucide-react";

const TITLES = {
  expired:        "EXPIRED CERTIFICATIONS",
  expiring_soon:  "EXPIRING SOON",
  current:        "CURRENT CERTIFICATIONS",
  all:            "ALL CERTIFICATIONS",
};

export default function OwnerCertifications() {
  const [sp] = useSearchParams();
  const status = sp.get("status") || "all";
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/owner/certifications?status=${status}`)
      .then((r) => setCerts(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [status]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return certs;
    return certs.filter((c) =>
      (c.cert_type || "").toLowerCase().includes(needle) ||
      (c.employee_name || "").toLowerCase().includes(needle) ||
      (c.company_name || "").toLowerCase().includes(needle) ||
      (c.employee_trade || "").toLowerCase().includes(needle) ||
      (c.issuer || "").toLowerCase().includes(needle)
    );
  }, [certs, q]);

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <Link to="/owner" data-testid="back-to-dashboard" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-safety-yellow mb-4">
        <ArrowLeft className="w-3 h-3" strokeWidth={3} /> Back to dashboard
      </Link>
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl" data-testid="cert-list-title">
            {TITLES[status] || TITLES.all}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {loading ? "Loading…" : `${rows.length} certification${rows.length === 1 ? "" : "s"} across all companies.`}
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="cert-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search employee, company, cert…"
            className="industrial-input pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider border-2 border-foreground/30 dark:border-safety-yellow/30">
          No certifications match.
        </div>
      ) : (
        <div className="border-2 border-foreground dark:border-safety-yellow overflow-x-auto bg-card">
          <table className="w-full text-sm" data-testid="cert-table">
            <thead>
              <tr className="bg-foreground/5 dark:bg-safety-yellow/10 border-b-2 border-foreground dark:border-safety-yellow">
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Status</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Certification</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Employee</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Trade</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Company</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Expires</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Issuer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0">
                  <td className="px-4 py-3"><TrafficLight status={c.status} size="sm" /></td>
                  <td className="px-4 py-3 font-bold">{c.cert_type}</td>
                  <td className="px-4 py-3">{c.employee_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{c.employee_trade || "—"}</td>
                  <td className="px-4 py-3 text-xs">{c.company_name || <span className="text-muted-foreground">Personal</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.expiration_date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{c.issuer || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
